// 📄 components/Configuracion/EndpointConfigCard.tsx
// Una fila de "Endpoints" dentro de ApiDefinitionCard.tsx. Cada endpoint
// se arma concatenando URL Base + API (de la ApiDefinition dueña — se
// piden una sola vez por API, no por endpoint, ver ApiDefinitionCard.tsx)
// + su propio path, p. ej. "vendorDataRequests", más params opcionales.
// Por eso urlBase/api llegan como props de solo lectura (para la vista
// previa) en vez de ser campos editables acá. `readOnly` sigue el mismo
// bloqueo de edición de la API dueña (ver ApiDefinitionCard.tsx).
import { Trash2, Plus, X, AlertTriangle } from "lucide-react";
import { ApiEndpointConfig, ApiEndpointMetodo } from "../../services/clienteConexionService.ts";
import { METODOS_HTTP } from "./apiTiposConfig.ts";
import { reemplazarPathVariables, extraerNombresPathVariables } from "../../utils/pathVariables.ts";

interface EndpointConfigCardProps {
  endpoint: ApiEndpointConfig;
  urlBase: string;
  api: string;
  readOnly: boolean;
  onChange: (endpoint: ApiEndpointConfig) => void;
  onRemove: () => void;
  // El catálogo de APIs (ver EndpointCollapsibleItem.tsx) envuelve esta
  // tarjeta en un header colapsable que ya trae su propio botón de
  // eliminar — este prop evita el ícono de basura duplicado ahí. Nadie
  // más lo pasa (ApiDefinitionCard.tsx/AribaApiTester.tsx quedan igual).
  ocultarBotonEliminar?: boolean;
}

const escaparRegex = (valor: string): string => valor.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Mantiene el Endpoint en sync con la clave que se está escribiendo en
// Path Variables, para que el usuario no tenga que escribir ":clave" a
// mano ahí (ver la confusión que causaba antes: una fila con valor
// cargado que no hacía nada porque el Endpoint no la mencionaba). Si la
// clave anterior ya estaba linkeada (":claveAnterior"), la renombra en su
// lugar — esto es lo que hace que escribir letra por letra funcione bien
// (cada keystroke es, en los hechos, un "rename" del char anterior).
// Si no hay match previo, la agrega al final. Nunca toca el Endpoint si
// la clave nueva ya aparece ahí (evita duplicar si el usuario la escribió
// a mano) ni si queda vacía (borrar la clave no borra la referencia del
// Endpoint — para eso está el botón de quitar la fila).
const insertarPathVariableEnEndpoint = (endpointPath: string, claveAnterior: string, claveNueva: string): string => {
  const anterior = claveAnterior.trim();
  const nueva = claveNueva.trim();
  if (!nueva) return endpointPath;

  const yaExiste = new RegExp(`:${escaparRegex(nueva)}\\b`).test(endpointPath);
  if (yaExiste) return endpointPath;

  if (anterior && anterior !== nueva) {
    const regexAnterior = new RegExp(`:${escaparRegex(anterior)}\\b`);
    if (regexAnterior.test(endpointPath)) {
      return endpointPath.replace(regexAnterior, `:${nueva}`);
    }
  }

  const base = endpointPath.trim().replace(/\/+$/, "");
  return `${base}/:${nueva}`;
};

export const construirUrlPreview = (urlBase: string, api: string, endpoint: ApiEndpointConfig): string => {
  const base = urlBase.trim().replace(/\/+$/, "");
  const apiPath = api.trim().replace(/^\/+|\/+$/g, "");
  const pathConVariables = reemplazarPathVariables(endpoint.endpoint, endpoint.pathVariables || []);
  const path = pathConVariables.trim().replace(/^\/+|\/+$/g, "");
  const url = [base, apiPath, path].filter(Boolean).join("/");

  const params = endpoint.params.filter((p) => p.clave.trim());
  if (params.length === 0) return url;

  const query = params.map((p) => `${p.clave.trim()}=${p.valor.trim()}`).join("&");
  return `${url}?${query}`;
};

export default function EndpointConfigCard({ endpoint, urlBase, api, readOnly, onChange, onRemove, ocultarBotonEliminar = false }: EndpointConfigCardProps) {
  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  const update = (field: keyof ApiEndpointConfig, value: string) => {
    onChange({ ...endpoint, [field]: value });
  };

  const addParam = () => {
    onChange({ ...endpoint, params: [...endpoint.params, { clave: "", valor: "" }] });
  };

  const updateParam = (index: number, field: "clave" | "valor", value: string) => {
    const params = endpoint.params.map((p, i) => (i === index ? { ...p, [field]: value } : p));
    onChange({ ...endpoint, params });
  };

  const removeParam = (index: number) => {
    onChange({ ...endpoint, params: endpoint.params.filter((_, i) => i !== index) });
  };

  // endpoint.pathVariables puede no existir en endpoints guardados antes
  // de que este campo existiera — se trata como [] en vez de romper.
  const pathVariables = endpoint.pathVariables || [];

  // Los nombres de Path Variables los define el path escrito en Endpoint
  // (":nombre", ver utils/pathVariables.ts) — una fila de esta lista cuya
  // clave no aparezca ahí queda "huérfana": no se usa en la URL ni
  // habilita el tab correspondiente en el Tester (ver AribaApiTester.tsx),
  // aunque tenga un valor cargado. Se avisa acá para no repetir la
  // confusión de cargar el valor sin haber escrito ":clave" en Endpoint.
  const nombresEnEndpoint = extraerNombresPathVariables(endpoint.endpoint);
  const esHuerfana = (clave: string): boolean => clave.trim() !== "" && !nombresEnEndpoint.includes(clave.trim());

  const addPathVariable = () => {
    onChange({ ...endpoint, pathVariables: [...pathVariables, { clave: "", valor: "" }] });
  };

  const updatePathVariable = (index: number, field: "clave" | "valor", value: string) => {
    const pathVariablesActualizadas = pathVariables.map((v, i) => (i === index ? { ...v, [field]: value } : v));

    if (field === "clave") {
      const endpointActualizado = insertarPathVariableEnEndpoint(endpoint.endpoint, pathVariables[index].clave, value);
      onChange({ ...endpoint, endpoint: endpointActualizado, pathVariables: pathVariablesActualizadas });
      return;
    }

    onChange({ ...endpoint, pathVariables: pathVariablesActualizadas });
  };

  const removePathVariable = (index: number) => {
    onChange({ ...endpoint, pathVariables: pathVariables.filter((_, i) => i !== index) });
  };

  // endpoint.headers puede no existir en endpoints guardados antes de que
  // este campo existiera — se trata como [] en vez de romper.
  const headers = endpoint.headers || [];

  const addHeader = () => {
    onChange({ ...endpoint, headers: [...headers, { clave: "", valor: "" }] });
  };

  const updateHeader = (index: number, field: "clave" | "valor", value: string) => {
    onChange({ ...endpoint, headers: headers.map((h, i) => (i === index ? { ...h, [field]: value } : h)) });
  };

  const removeHeader = (index: number) => {
    onChange({ ...endpoint, headers: headers.filter((_, i) => i !== index) });
  };

  return (
    <div className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <label className={labelClass}>Nombre (alias)</label>
          <input
            type="text"
            className={inputClass}
            value={endpoint.nombre}
            onChange={(e) => update("nombre", e.target.value)}
            placeholder="Ej: Vendor Data Request"
            disabled={readOnly}
          />
        </div>
        {!readOnly && !ocultarBotonEliminar && (
          <button
            type="button"
            onClick={onRemove}
            className="mt-6 p-2.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
            title="Eliminar endpoint"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-4">
        <div>
          <label className={labelClass}>Método</label>
          <select
            className={inputClass}
            value={endpoint.metodo}
            onChange={(e) => onChange({ ...endpoint, metodo: e.target.value as ApiEndpointMetodo })}
            disabled={readOnly}
          >
            {METODOS_HTTP.map((metodo) => (
              <option key={metodo} value={metodo}>{metodo}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Endpoint</label>
          <input
            type="text"
            className={inputClass}
            value={endpoint.endpoint}
            onChange={(e) => update("endpoint", e.target.value)}
            placeholder="AttachmentContentSet"
            disabled={readOnly}
          />
        </div>
      </div>

      {(endpoint.metodo === "POST" || endpoint.metodo === "PUT") && (
        <div>
          <label className={labelClass}>Body de ejemplo</label>
          <textarea
            className={`${inputClass} font-mono resize-y`}
            rows={4}
            value={endpoint.bodyEjemplo}
            onChange={(e) => update("bodyEjemplo", e.target.value)}
            placeholder={'{\n  "campo": "valor"\n}'}
            disabled={readOnly}
          />
        </div>
      )}

      <div>
        <label className={labelClass}>Path Variables</label>
        <p className="text-[11px] text-gray-500 mb-2">Ej: usa <span className="font-mono text-gray-400">:vendorId</span> en el Endpoint y define su valor acá.</p>
        <div className="space-y-2">
          {pathVariables.map((pv, index) => {
            const huerfana = esHuerfana(pv.clave);
            return (
              <div key={index}>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    className={`${inputClass} font-mono ${huerfana ? "border-amber-500/50" : ""}`}
                    value={pv.clave}
                    onChange={(e) => updatePathVariable(index, "clave", e.target.value)}
                    placeholder="Ej: vendorId"
                    disabled={readOnly}
                  />
                  <input
                    type="text"
                    className={inputClass}
                    value={pv.valor}
                    onChange={(e) => updatePathVariable(index, "valor", e.target.value)}
                    placeholder="Valor"
                    disabled={readOnly}
                  />
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => removePathVariable(index)}
                      className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                      title="Quitar path variable"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {huerfana && (
                  <p className="text-[11px] text-amber-400 flex items-center gap-1 mt-1">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    "{pv.clave}" no aparece como <span className="font-mono">:{pv.clave.trim()}</span> en el Endpoint — no se usará hasta que lo agregues ahí.
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={addPathVariable}
            className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Agregar path variable
          </button>
        )}
      </div>

      <div>
        <label className={labelClass}>Params</label>
        <div className="space-y-2">
          {endpoint.params.map((param, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                className={inputClass}
                value={param.clave}
                onChange={(e) => updateParam(index, "clave", e.target.value)}
                placeholder="Nombre del param"
                disabled={readOnly}
              />
              <input
                type="text"
                className={inputClass}
                value={param.valor}
                onChange={(e) => updateParam(index, "valor", e.target.value)}
                placeholder="Valor"
                disabled={readOnly}
              />
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => removeParam(index)}
                  className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                  title="Quitar param"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={addParam}
            className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Agregar param
          </button>
        )}
      </div>

      <div>
        <label className={labelClass}>Cabeceras</label>
        <div className="space-y-2">
          {headers.map((header, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                className={inputClass}
                value={header.clave}
                onChange={(e) => updateHeader(index, "clave", e.target.value)}
                placeholder="Nombre de la cabecera"
                disabled={readOnly}
              />
              <input
                type="text"
                className={inputClass}
                value={header.valor}
                onChange={(e) => updateHeader(index, "valor", e.target.value)}
                placeholder="Valor"
                disabled={readOnly}
              />
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => removeHeader(index)}
                  className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                  title="Quitar cabecera"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={addHeader}
            className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Agregar cabecera
          </button>
        )}
      </div>

      {(urlBase || api || endpoint.endpoint) && (
        <div className="bg-black/30 border border-white/5 rounded-lg p-2.5 font-mono text-[11px] text-emerald-400 break-all">
          {construirUrlPreview(urlBase, api, endpoint)}
        </div>
      )}
    </div>
  );
}
